import type { ReactNode } from "react"
import { useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconEnvelopeOutline18 as Envelope,
	IconHourglassOutline18 as Hourglass,
	IconInboxOutline18 as Inbox,
} from "nucleo-ui-outline-18"

import type {
	NotificationCategory,
	NotificationType,
} from "@productlane/lib/src/browser"
import {
	NOTIFICATION_CATEGORIES,
	NOTIFICATION_CATEGORY_COPY,
	NOTIFICATION_TYPE_COPY,
	NOTIFICATION_TYPES,
	notificationTypeKeys,
	parseNotificationSettings,
	resolveTypePreference,
} from "@productlane/lib/src/browser"
import {
	AlertDialog,
	Badge,
	Button,
	Checkbox,
	Select,
	SlackIconGray,
	Switch,
} from "@productlane/ui"

import type { ScopeOptionValue } from "../../settings/personal-notification-settings/notification-scope-options"
import { isDevEnvironment } from "@/env"
import { NOTIFICATION_SETTINGS_COPY as copy } from "@/screens/app/notifications/copy"
import { NotFound } from "@/screens/not-found"
import {
	scopeOptionsFor,
	scopeOptionValue,
} from "../../settings/personal-notification-settings/notification-scope-options"
import { SettingsHeader } from "../../settings/settings-header"
import {
	settingsNavGroupClass,
	SettingsNavRow,
} from "../../settings/settings-pages/settings-nav-row"
import { SettingItem } from "../../settings/settings-switches/setting-item"
import {
	SettingsCardInsetContext,
	SettingsContainer,
} from "../../settings/settings-switches/settings-container"
import { DesignIssueHeader } from "./design-issue-header"

type Version = "A" | "B" | "C"
const VERSIONS: Version[] = ["A", "B", "C"]

const REPLY_TIME_HOURS = 48
const MOCK_EMAIL = "you@acme.com"
const MOCK_SLACK_TEAM = "Acme"
const MOCK_VIEWS = [
	{ id: "managed", name: "Managed accounts" },
	{ id: "community", name: "Slack community" },
]

// The new inbox type, shown next to the SLA types it sits beside in the registry.
const REPLY_TIME_MISSED = "thread.reply_time_missed"
const REPLY_TIME_MISSED_COPY = {
	label: "Reply time missed",
	description: `No reply within your ${REPLY_TIME_HOURS} hour reply time`,
}
// No registry entry yet; it takes the same scopes as the SLA types.
const REPLY_TIME_MISSED_OPTIONS = scopeOptionsFor(
	NOTIFICATION_TYPES["thread.sla_breached"],
	MOCK_VIEWS,
)
const SLA_TYPES = ["thread.sla_at_risk", "thread.sla_breached"]
const REPLY_TIME_TYPES = [REPLY_TIME_MISSED, ...SLA_TYPES]

function TitleBadge({
	children,
	badge = "New",
}: {
	children: ReactNode
	badge?: "New" | "Changed"
}) {
	return (
		<span className="flex items-center gap-2">
			{children}
			<Badge variant={badge === "New" ? "green" : "default"} size="sm">
				{badge}
			</Badge>
		</span>
	)
}

function GroupLabel({ children }: { children: ReactNode }) {
	return <div className="pb-3 pt-8 text-sm font-medium">{children}</div>
}

// One screen in three versions, side by side, so a column reads as one version.
function VersionRow({
	label,
	width,
	notes,
	children,
}: {
	label: string
	width: number
	notes: Record<Version, string>
	children: (version: Version) => ReactNode
}) {
	return (
		<div className="space-y-4">
			<div className="text-sm font-medium text-tertiary">{label}</div>
			<div className="flex flex-nowrap items-start gap-10 overflow-x-auto pb-4">
				{VERSIONS.map((version) => (
					<div
						key={version}
						className="flex-none space-y-3"
						style={{ width: width + 64 }}
					>
						<div>
							<div className="text-sm font-semibold text-primary">
								Version {version}
							</div>
							<div className="text-sm text-secondary">{notes[version]}</div>
						</div>
						<div className="rounded-xl bg-gray-50 px-8 py-10 ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
							{children(version)}
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

interface ToggleRow {
	field: string
	title: string
	description: string
	isNew?: boolean
}

interface ToggleGroup {
	label: string
	rows: ToggleRow[]
	badge?: "New" | "Changed"
}

function ToggleRows({
	rows,
	values,
	onToggle,
}: {
	rows: ToggleRow[]
	values: Record<string, boolean>
	onToggle: (field: string, value: boolean) => void
}) {
	return (
		<SettingsContainer>
			{rows.map((row, index) => (
				<SettingItem
					key={row.field}
					className={index > 0 ? "pt-4" : undefined}
					title={row.isNew ? <TitleBadge>{row.title}</TitleBadge> : row.title}
					description={row.description}
					action={
						<Switch
							name={row.field}
							checked={values[row.field] ?? false}
							onCheckedChange={(value) => onToggle(row.field, value)}
						/>
					}
				/>
			))}
		</SettingsContainer>
	)
}

function ToggleGroups({
	groups,
	values,
	setValues,
}: {
	groups: ToggleGroup[]
	values: Record<string, boolean>
	setValues: (values: Record<string, boolean>) => void
}) {
	return (
		<>
			{groups.map((group) => (
				<div key={group.label}>
					<GroupLabel>
						{group.badge ? (
							<TitleBadge badge={group.badge}>{group.label}</TitleBadge>
						) : (
							group.label
						)}
					</GroupLabel>
					<ToggleRows
						rows={group.rows}
						values={values}
						onToggle={(field, value) =>
							setValues({ ...values, [field]: value })
						}
					/>
				</div>
			))}
		</>
	)
}

// Version C: the channel page points to the one place reply times are set.
function ReplyTimesLinkRow() {
	return (
		<div className="pt-8">
			<div className={settingsNavGroupClass}>
				<SettingsNavRow
					icon={Hourglass}
					title="Reply times"
					description="Set for inbox, email and Slack on the Notifications page."
					link="/settings/notifications"
				/>
			</div>
		</div>
	)
}

const EMAIL_THREAD_GROUP: ToggleGroup = {
	label: "Feedback and Threads",
	rows: [
		{
			field: "newThreadNotificationsEnabled",
			title: "New feedback",
			description: "When new feedback is submitted",
		},
		{
			field: "aiHandoffNotificationsEnabled",
			title: "Productlane Agent handoffs",
			description:
				"When the Productlane Agent tells a customer the team will follow up",
		},
		{
			field: "assignedThreadsReplyReceivedNotificationsEnabled",
			title: "New replies",
			description: "When someone replies to your assigned threads",
		},
		{
			field: "commentMentionsNotificationsEnabled",
			title: "Comment mentions",
			description: "When you're mentioned in comments",
		},
		{
			field: "assignmentChangesNotificationsEnabled",
			title: "Assignment changes",
			description: "When thread assignments change",
		},
		{
			field: "unassignedThreadsReplyReceivedNotificationsEnabled",
			title: "Unassigned thread replies",
			description: "When someone replies to unassigned threads",
		},
	],
}

const EMAIL_REPLY_ROWS: ToggleRow[] = [
	{
		field: "replyTimeMissedEmailsEnabled",
		title: "Reply time missed",
		description: "When a thread waits past your reply time",
		isNew: true,
	},
	{
		field: "slaAtRiskEmailsEnabled",
		title: "SLA at risk",
		description: "When a thread is within a day of its SLA deadline",
		isNew: true,
	},
	{
		field: "slaBreachedEmailsEnabled",
		title: "SLA breached",
		description: "When a thread passes its SLA deadline without a reply",
		isNew: true,
	},
]

const EMAIL_CONTENT_GROUP: ToggleGroup = {
	label: "Content and Summaries",
	rows: [
		{
			field: "changelogEmailsEnabled",
			title: "New Changelog draft",
			description: "When new changelog drafts are created",
		},
		{
			field: "docsProposalEmailsEnabled",
			title: "Daily summary of docs drafts",
			description:
				"Docs your agent drafted, with accept and decline buttons in the email",
		},
		{
			field: "upvoteEmailsEnabled",
			title: "Daily summary of new upvotes",
			description: "A daily summary of upvotes on your portal",
		},
	],
}

function emailGroups(version: Version): ToggleGroup[] {
	if (version === "A") {
		return [
			EMAIL_THREAD_GROUP,
			{ label: "SLA", rows: EMAIL_REPLY_ROWS },
			EMAIL_CONTENT_GROUP,
		]
	}
	if (version === "B") {
		return [
			{ label: "Reply times", rows: EMAIL_REPLY_ROWS, badge: "New" },
			EMAIL_THREAD_GROUP,
			EMAIL_CONTENT_GROUP,
		]
	}
	return [EMAIL_THREAD_GROUP, EMAIL_CONTENT_GROUP]
}

const SLACK_ASSIGNED_GROUP: ToggleGroup = {
	label: "Threads assigned to you",
	rows: [
		{
			field: "followUpMessageNotificationsEnabled",
			title: "Follow-up message",
			description: "When someone follows up on your assigned threads",
		},
		{
			field: "assignmentChangesNotificationsEnabled",
			title: "Assignment changes",
			description: "When thread assignments change",
		},
		{
			field: "commentCreatedNotificationsEnabled",
			title: "Comment created",
			description: "When new comments are added to your threads",
		},
	],
}

const SLACK_ALL_GROUP: ToggleGroup = {
	label: "All threads",
	rows: [
		{
			field: "newThreadNotificationsEnabled",
			title: "New thread",
			description: "When new threads are created",
		},
		{
			field: "aiHandoffNotificationsEnabled",
			title: "Productlane Agent handoffs",
			description:
				"When the Productlane Agent tells a customer the team will follow up",
		},
		{
			field: "commentMentionsNotificationsEnabled",
			title: "Comment mentions",
			description: "When you're mentioned in comments",
		},
		{
			field: "unassignedThreadReplyNotificationsEnabled",
			title: "Unassigned thread replies",
			description: "When someone replies to unassigned threads",
		},
	],
}

const SLACK_REPLY_ROWS: ToggleRow[] = [
	{
		field: "replyTimeMissedNotificationsEnabled",
		title: "Reply time missed",
		description: "When a thread waits past your reply time",
		isNew: true,
	},
	{
		field: "slaAtRiskNotificationsEnabled",
		title: "SLA at risk",
		description: "When a thread is within a day of its SLA deadline",
	},
	{
		field: "slaBreachedNotificationsEnabled",
		title: "SLA breached",
		description: "When a thread passes its SLA deadline without a reply",
	},
]

const SLACK_DOCS_GROUP: ToggleGroup = {
	label: "Docs",
	rows: [
		{
			field: "docsProposalNotificationsEnabled",
			title: "Daily summary of docs drafts",
			description:
				"Docs your agent drafted, with accept and decline buttons in the message",
		},
	],
}

function slackGroups(version: Version): ToggleGroup[] {
	if (version === "A") {
		return [
			SLACK_ASSIGNED_GROUP,
			SLACK_ALL_GROUP,
			{ label: "SLA", rows: SLACK_REPLY_ROWS },
			SLACK_DOCS_GROUP,
		]
	}
	if (version === "B") {
		return [
			{ label: "Reply times", rows: SLACK_REPLY_ROWS, badge: "Changed" },
			SLACK_ASSIGNED_GROUP,
			SLACK_ALL_GROUP,
			SLACK_DOCS_GROUP,
		]
	}
	return [SLACK_ASSIGNED_GROUP, SLACK_ALL_GROUP, SLACK_DOCS_GROUP]
}

function allOn(groups: ToggleGroup[]): Record<string, boolean> {
	const values: Record<string, boolean> = {}
	for (const group of groups) {
		for (const row of group.rows) values[row.field] = true
	}
	return values
}

function anyOn(values: Record<string, boolean>) {
	return Object.values(values).some(Boolean)
}

const CHANNELS = ["Inbox", "Email", "Slack"] as const
type Channel = (typeof CHANNELS)[number]

const REPLY_EVENTS = [
	{
		id: "replyTimeMissed",
		title: "Reply time missed",
		description: `No reply within ${REPLY_TIME_HOURS} hours`,
		isNew: true,
	},
	{
		id: "slaAtRisk",
		title: "SLA at risk",
		description: "A reply is due soon",
	},
	{
		id: "slaBreached",
		title: "SLA breached",
		description: "A reply is overdue",
	},
]

function initialMatrix() {
	const matrix: Record<string, Record<Channel, boolean>> = {}
	for (const event of REPLY_EVENTS) {
		matrix[event.id] = { Inbox: true, Email: true, Slack: true }
	}
	return matrix
}

// Version C: every reply time event and every channel on one card.
function ReplyTimesCard() {
	const [matrix, setMatrix] = useState(initialMatrix)
	const [unassigned, setUnassigned] = useState(false)

	return (
		<>
			<GroupLabel>
				<TitleBadge>Reply times</TitleBadge>
			</GroupLabel>
			<SettingsContainer>
				<div className="grid grid-cols-[1fr_repeat(3,56px)] items-center text-xs text-tertiary">
					<span />
					{CHANNELS.map((channel) => (
						<span key={channel} className="text-center">
							{channel}
						</span>
					))}
				</div>
				{REPLY_EVENTS.map((event) => (
					<div
						key={event.id}
						className="grid grid-cols-[1fr_repeat(3,56px)] items-center pt-3"
					>
						<div className="flex flex-col gap-0.5">
							<span className="text-sm font-medium text-primary">
								{event.isNew ? (
									<TitleBadge>{event.title}</TitleBadge>
								) : (
									event.title
								)}
							</span>
							<span className="text-xs text-secondary">
								{event.description}
							</span>
						</div>
						{CHANNELS.map((channel) => (
							<div key={channel} className="flex justify-center">
								<Checkbox
									aria-label={`${event.title} by ${channel}`}
									checked={matrix[event.id]?.[channel] ?? false}
									onCheckedChange={(value) =>
										setMatrix({
											...matrix,
											[event.id]: {
												...(matrix[event.id] ?? {
													Inbox: false,
													Email: false,
													Slack: false,
												}),
												[channel]: value === true,
											},
										})
									}
								/>
							</div>
						))}
					</div>
				))}
				<SettingItem
					className="pt-4"
					title="Unassigned threads too"
					description="Also alert me when nobody is assigned"
					action={
						<Switch
							name="replyTimesUnassigned"
							checked={unassigned}
							onCheckedChange={setUnassigned}
						/>
					}
				/>
			</SettingsContainer>
		</>
	)
}

function OverviewPage({ version }: { version: Version }) {
	return (
		<>
			<SettingsHeader
				title="Notifications"
				subtitle={copy.overviewSubtitle}
				docsArticle="c3daae2e-cb81-4c01-8020-329f825916d9"
			/>
			<div className={settingsNavGroupClass}>
				<SettingsNavRow
					icon={Inbox}
					title={copy.navTitle}
					description={copy.navDescription}
					link="/settings/notifications/inbox"
				/>
				<SettingsNavRow
					icon={Envelope}
					title="Email"
					description="Get an email for the events you pick."
					status={true}
					link="/settings/notifications/email"
				/>
				<SettingsNavRow
					icon={SlackIconGray}
					title="Slack"
					description="Get a direct message in Slack for the events you pick."
					status={true}
					link="/settings/notifications/slack"
				/>
			</div>
			{version === "C" && <ReplyTimesCard />}
		</>
	)
}

const DEFAULT_SETTINGS = parseNotificationSettings(undefined)

function typesByCategory(version: Version) {
	const map = new Map<NotificationCategory, string[]>()
	for (const type of notificationTypeKeys()) {
		if (version !== "A" && SLA_TYPES.includes(type)) continue
		const { category } = NOTIFICATION_TYPES[type]
		const list = [...(map.get(category) ?? [])]
		if (version === "A" && type === "thread.sla_at_risk") {
			list.push(REPLY_TIME_MISSED)
		}
		list.push(type)
		map.set(category, list)
	}
	return map
}

function initialScopes(): Record<string, ScopeOptionValue> {
	const scopes: Record<string, ScopeOptionValue> = {
		[REPLY_TIME_MISSED]: "involved",
	}
	for (const type of notificationTypeKeys()) {
		scopes[type] = scopeOptionValue(
			resolveTypePreference(type, DEFAULT_SETTINGS),
		)
	}
	return scopes
}

function ScopeRows({
	types,
	scopes,
	setScopes,
}: {
	types: string[]
	scopes: Record<string, ScopeOptionValue>
	setScopes: (scopes: Record<string, ScopeOptionValue>) => void
}) {
	return (
		<SettingsContainer>
			{types.map((type, index) => {
				const isNew = type === REPLY_TIME_MISSED
				const typeCopy = isNew
					? REPLY_TIME_MISSED_COPY
					: NOTIFICATION_TYPE_COPY[type]
				const options = isNew
					? REPLY_TIME_MISSED_OPTIONS
					: scopeOptionsFor(
							NOTIFICATION_TYPES[type as NotificationType],
							MOCK_VIEWS,
						)
				const label = typeCopy?.label ?? type
				return (
					<SettingItem
						key={type}
						className={index > 0 ? "pt-4" : undefined}
						title={isNew ? <TitleBadge>{label}</TitleBadge> : label}
						description={typeCopy?.description}
						action={
							<Select
								name={`notification-${type}`}
								className="w-40 flex-none"
								popoverAlign="end"
								value={scopes[type]}
								options={options}
								onChange={(value) =>
									setScopes({
										...scopes,
										[type]: value as ScopeOptionValue,
									})
								}
							/>
						}
					/>
				)
			})}
		</SettingsContainer>
	)
}

function InboxPage({ version }: { version: Version }) {
	const [desktop, setDesktop] = useState(false)
	const [scopes, setScopes] = useState(initialScopes)
	const byCategory = typesByCategory(version)

	return (
		<>
			<SettingsHeader title={copy.pageTitle} subtitle={copy.pageSubtitle} />

			<SettingsContainer>
				<SettingItem
					title={copy.desktopTitle}
					description={copy.desktopDescription}
					action={
						<Switch
							name="notification-desktop"
							aria-label={copy.desktopTitle}
							checked={desktop}
							onCheckedChange={setDesktop}
						/>
					}
				/>
			</SettingsContainer>

			{version === "B" && (
				<div>
					<GroupLabel>
						<TitleBadge>Reply times</TitleBadge>
					</GroupLabel>
					<ScopeRows
						types={REPLY_TIME_TYPES}
						scopes={scopes}
						setScopes={setScopes}
					/>
				</div>
			)}
			{version === "C" && <ReplyTimesLinkRow />}

			{NOTIFICATION_CATEGORIES.map((category) => {
				const types = byCategory.get(category) ?? []
				if (types.length === 0) return null
				return (
					<div key={category}>
						<GroupLabel>
							{NOTIFICATION_CATEGORY_COPY[category].label}
						</GroupLabel>
						<ScopeRows types={types} scopes={scopes} setScopes={setScopes} />
					</div>
				)
			})}
		</>
	)
}

function EmailPage({ version }: { version: Version }) {
	const groups = emailGroups(version)
	const [values, setValues] = useState(() => allOn(groups))
	const [weeklyRoadmap, setWeeklyRoadmap] = useState(true)

	return (
		<>
			<SettingsHeader title="Email" subtitle="Pick what sends an email." />

			<GroupLabel>Workspace</GroupLabel>
			<SettingsContainer>
				<SettingItem
					title="Weekly roadmap email"
					description="A short survey to the people who upvoted or wrote feedback on your portal. It sends only in a week where you added a project or an issue."
					action={
						<Switch
							name="weeklyRoadmapEmailEnabled"
							checked={weeklyRoadmap}
							onCheckedChange={setWeeklyRoadmap}
						/>
					}
				/>
			</SettingsContainer>

			<SettingsContainer>
				<SettingItem
					title="Email notifications"
					description={`Emails go to ${MOCK_EMAIL}`}
					action={
						<Switch
							name="emailNotifications"
							checked={anyOn(values)}
							onCheckedChange={(value) =>
								setValues(
									Object.fromEntries(
										Object.keys(allOn(groups)).map((k) => [k, value]),
									),
								)
							}
						/>
					}
				/>
			</SettingsContainer>

			{version === "C" && <ReplyTimesLinkRow />}
			<ToggleGroups groups={groups} values={values} setValues={setValues} />
		</>
	)
}

function SlackPage({ version }: { version: Version }) {
	const groups = slackGroups(version)
	const [values, setValues] = useState(() => allOn(groups))
	const [isDisconnectOpen, setIsDisconnectOpen] = useState(false)

	return (
		<>
			<SettingsHeader
				title="Slack"
				subtitle="Get a direct message in Slack for the events you pick."
			/>

			<SettingsContainer>
				<SettingItem
					title="Slack notifications"
					description={`Direct messages in ${MOCK_SLACK_TEAM}`}
					action={
						<Switch
							name="slackNotifications"
							checked={anyOn(values)}
							onCheckedChange={(value) =>
								setValues(
									Object.fromEntries(
										Object.keys(allOn(groups)).map((k) => [k, value]),
									),
								)
							}
						/>
					}
				/>
			</SettingsContainer>

			{version === "C" && <ReplyTimesLinkRow />}
			<ToggleGroups groups={groups} values={values} setValues={setValues} />

			<div className="mt-8 flex items-center justify-between gap-4">
				<span className="text-sm text-secondary">
					Connected to {MOCK_SLACK_TEAM}
				</span>
				<Button
					variant="outline"
					size="sm"
					onClick={() => setIsDisconnectOpen(true)}
				>
					Disconnect
				</Button>
			</div>

			<AlertDialog
				isOpen={isDisconnectOpen}
				onOpenChange={setIsDisconnectOpen}
				variant="positive"
				confirmText="Disconnect"
				title="Disconnect Slack?"
				description="You'll stop receiving personal Slack notifications. Reconnect any time to turn them back on."
				onCancel={() => setIsDisconnectOpen(false)}
				onConfirm={() => setIsDisconnectOpen(false)}
			/>
		</>
	)
}

const THREAD = {
	title: "Webhooks fail after the SSO change",
	contact: "Maya Chen",
	company: "Acme",
	message:
		"Since we moved to Okta SSO yesterday, every webhook call returns 401. Can someone take a look today?",
}

const DIGEST = [
	{ ...THREAD, waited: 50 },
	{
		title: "Invoice shows the old VAT number",
		contact: "Tom Berger",
		company: "Northwind",
		waited: 49,
	},
	{
		title: "Export to CSV skips archived rows",
		contact: "Lena Vogt",
		company: "Globex",
		waited: 48,
	},
]

const HEADLINE = `Reply time missed: ${THREAD.title}`
const TIMING = `Waiting 50 hours. Your reply time is ${REPLY_TIME_HOURS} hours.`
const DIGEST_HEADLINE = `${DIGEST.length} threads are past your ${REPLY_TIME_HOURS} hour reply time`

function SlackButton({
	children,
	onClick,
}: {
	children: ReactNode
	onClick?: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="h-7 rounded-md border border-[#1d1c1d]/30 bg-white px-3 text-[13px] font-bold text-[#1d1c1d] hover:bg-[#f8f8f8]"
		>
			{children}
		</button>
	)
}

function SlackFrame({ children }: { children: ReactNode }) {
	return (
		<div className="overflow-hidden rounded-lg bg-white font-[Lato,ui-sans-serif,system-ui] shadow-md ring-1 ring-black/5 dark:ring-white/10">
			<div className="flex h-12 items-center border-b border-gray-200 px-5">
				<span className="text-[15px] font-bold text-[#1d1c1d]">
					Productlane
				</span>
			</div>
			<div className="flex gap-2 px-5 py-3">
				<div className="mt-0.5 flex size-9 flex-none items-center justify-center rounded-md bg-gray-900 text-sm font-bold text-white">
					P
				</div>
				<div className="min-w-0 flex-1 space-y-1">
					<div className="flex items-baseline gap-1.5">
						<span className="text-[15px] font-bold text-[#1d1c1d]">
							Productlane
						</span>
						<span className="rounded-sm bg-[#1d1c1d]/[0.08] px-1 text-[10px] font-semibold text-[#616061]">
							APP
						</span>
						<span className="text-xs text-[#616061]">10:42 AM</span>
					</div>
					{children}
				</div>
			</div>
		</div>
	)
}

function SlackDm({ version }: { version: Version }) {
	const [done, setDone] = useState(false)

	if (version === "B") {
		return (
			<SlackFrame>
				<div className="text-[15px] text-[#1d1c1d]">
					<span className="font-bold">{HEADLINE}</span>
				</div>
				<div className="text-[13px] text-[#616061]">
					{THREAD.contact} at {THREAD.company} · waiting 50 hours
				</div>
				<div className="pt-1">
					<SlackButton>Open</SlackButton>
				</div>
			</SlackFrame>
		)
	}

	if (version === "C") {
		return (
			<SlackFrame>
				<div className="text-[15px] font-bold text-[#1d1c1d]">
					{DIGEST_HEADLINE}
				</div>
				<div className="space-y-2 py-1">
					{DIGEST.map((thread) => (
						<div key={thread.title} className="text-[15px] text-[#1d1c1d]">
							<span className="font-bold text-[#1264a3]">{thread.title}</span>
							<div className="text-[13px] text-[#616061]">
								{thread.contact} · {thread.company} · waiting {thread.waited}{" "}
								hours
							</div>
						</div>
					))}
				</div>
				<div className="pt-1">
					<SlackButton>Open inbox</SlackButton>
				</div>
			</SlackFrame>
		)
	}

	return (
		<SlackFrame>
			<div className="text-[15px] font-bold text-[#1d1c1d]">{HEADLINE}</div>
			<div className="text-[13px] text-[#616061]">
				{THREAD.contact} · {THREAD.company} · Assigned to you
			</div>
			<div className="my-1 border-l-4 border-[#dddddd] pl-3 text-[15px] leading-[22px] text-[#1d1c1d]">
				{THREAD.message}
			</div>
			<div className="text-[13px] text-[#616061]">{TIMING}</div>
			{done && (
				<div className="flex items-center gap-1 text-[13px] text-[#616061]">
					<Check className="size-3.5 flex-none" />
					Marked done by you
				</div>
			)}
			<div className="flex items-center gap-2 pt-1">
				<SlackButton>Open</SlackButton>
				<SlackButton onClick={() => setDone(!done)}>
					{done ? "Reopen" : "Mark done"}
				</SlackButton>
			</div>
		</SlackFrame>
	)
}

function EmailButton({ children }: { children: ReactNode }) {
	return (
		<span className="inline-block rounded-full bg-[#0A0A0A] px-[22px] py-3 text-sm font-medium text-white">
			{children}
		</span>
	)
}

function EmailPreview({ version }: { version: Version }) {
	const subject = version === "C" ? DIGEST_HEADLINE : HEADLINE
	return (
		<div className="overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black/5 dark:ring-white/10">
			<div className="space-y-1 border-b border-gray-200 px-6 py-4 text-[13px] text-gray-600">
				<div>
					<span className="text-gray-400">From</span> Productlane
					{" <notifications@productlane.com>"}
				</div>
				<div className="font-medium text-gray-900">{subject}</div>
			</div>
			<div className="space-y-4 px-6 py-6 text-sm text-gray-800">
				<div className="text-base font-semibold text-gray-900">Productlane</div>
				{version === "A" && (
					<>
						<p>
							{`"${THREAD.title}"`} from {THREAD.contact} at {THREAD.company} is
							waiting for a reply. {TIMING}
						</p>
						<p className="border-l-2 border-gray-200 pl-3 text-gray-600">
							{THREAD.message}
						</p>
						<div>
							<EmailButton>Open thread</EmailButton>
						</div>
					</>
				)}
				{version === "B" && (
					<>
						<p>
							{THREAD.contact} at {THREAD.company} has waited 50 hours for a
							reply.
						</p>
						<div>
							<EmailButton>Open thread</EmailButton>
						</div>
					</>
				)}
				{version === "C" && (
					<>
						<div className="divide-y divide-gray-200 rounded-lg ring-1 ring-gray-200">
							{DIGEST.map((thread) => (
								<div key={thread.title} className="px-4 py-3">
									<div className="font-medium text-gray-900">
										{thread.title}
									</div>
									<div className="text-xs text-gray-500">
										{thread.contact} · {thread.company} · waiting{" "}
										{thread.waited} hours
									</div>
								</div>
							))}
						</div>
						<div>
							<EmailButton>Open inbox</EmailButton>
						</div>
					</>
				)}
				<p className="text-xs text-gray-500">
					{version === "C"
						? "You get this because these threads are assigned to you. Manage it in Settings, Notifications."
						: "You get this because the thread is assigned to you. Manage it in Settings, Notifications."}
				</p>
			</div>
		</div>
	)
}

const SETTINGS_WIDTH = 704
const MESSAGE_WIDTH = 600

export function SlackAlertChannelsPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<SettingsCardInsetContext.Provider value={true}>
			<div className="min-h-screen space-y-12 bg-white px-10 py-10 pb-24 dark:bg-gray-950">
				<div className="max-w-4xl">
					<DesignIssueHeader
						identifier="PRO-10808"
						title="Personal email and Slack notifications when a thread misses its reply time or breaches its SLA"
						url="https://linear.app/productlane/issue/PRO-10808"
						summary="When a customer waits past your reply time or a thread breaches its SLA, the person responsible hears about it directly: in the inbox, by email, or as a Slack DM. It goes to the assignee, and unassigned threads go to people who follow all threads. Reply time works with or without SLA."
					/>
				</div>

				<VersionRow
					label="Settings / Notifications"
					width={SETTINGS_WIDTH}
					notes={{
						A: "Unchanged. Each channel page holds its own reply time rows.",
						B: "Unchanged. Each channel page holds its own reply time rows.",
						C: "One Reply times card: each event gets a box per channel.",
					}}
				>
					{(version) => <OverviewPage version={version} />}
				</VersionRow>

				<VersionRow
					label="Settings / Notifications / Inbox"
					width={SETTINGS_WIDTH}
					notes={{
						A: "Reply time missed sits beside the SLA rows under Threads.",
						B: "A Reply times group at the top, with both SLA rows moved in.",
						C: "SLA rows move to the overview. A link row points there.",
					}}
				>
					{(version) => <InboxPage version={version} />}
				</VersionRow>

				<VersionRow
					label="Settings / Notifications / Email"
					width={SETTINGS_WIDTH}
					notes={{
						A: "A new SLA group with all three rows, below the thread rows.",
						B: "A Reply times group first, right under the email switch.",
						C: "No new rows here. A link row points to the overview.",
					}}
				>
					{(version) => <EmailPage version={version} />}
				</VersionRow>

				<VersionRow
					label="Settings / Notifications / Slack"
					width={SETTINGS_WIDTH}
					notes={{
						A: "Reply time missed joins the existing SLA group.",
						B: "The SLA group becomes Reply times and moves to the top.",
						C: "SLA rows move to the overview. A link row points there.",
					}}
				>
					{(version) => <SlackPage version={version} />}
				</VersionRow>

				<VersionRow
					label="Slack DM"
					width={MESSAGE_WIDTH}
					notes={{
						A: "One message per thread, with the customer's words and Mark done.",
						B: "One short message per thread: who, how long, Open.",
						C: "One digest that lists every thread past your reply time.",
					}}
				>
					{(version) => <SlackDm version={version} />}
				</VersionRow>

				<VersionRow
					label="Email"
					width={MESSAGE_WIDTH}
					notes={{
						A: "One email per thread, with the customer's message quoted.",
						B: "One short email per thread: who, how long, Open thread.",
						C: "One digest email that lists every thread past your reply time.",
					}}
				>
					{(version) => <EmailPreview version={version} />}
				</VersionRow>
			</div>
		</SettingsCardInsetContext.Provider>
	)
}
